import { action, computed } from "mobx";
import { sumBy, toFinite } from "lodash";
import { FormErrors, FormStore } from "@modules/formx";
import {
  ConfimTravel,
  BookingHospitalityGet,
  BookingScheduleSearch_Get,
  GetTravelConfirmationPageDataQuery,
  UpdateTravelConfirmation,
  ConfirmTravelInput,
  UploadBlob
} from "@graphql";
import {
  dateToWeekYear,
  durationsToWeeks,
  weeksToDurations,
  WeekYear,
  weekYearToDate
} from "@bonliva-crm/common";
import { TravelConfirmationFormData, validationSchema } from "./utils";
import {
  parseWeekYear,
  weekYearToString
} from "@bookings/BookingHospitality/AccommodationDocumentUploader/AccommodationConfirmationStore";
import { globalStore } from "@store";
import { LegalPersonType } from "@bonliva-crm/shared";
import locale from "@bonliva-crm/locale";

export class TravelConfirmationStore {
  form: FormStore<TravelConfirmationFormData>;

  get isNew() {
    return !this.confirmationId;
  }

  constructor(
    public confirmationId: string | null,
    public bookingId: string,
    public bookingData: GetTravelConfirmationPageDataQuery["bookingById"],
    initialValues: TravelConfirmationFormData
    // week: WeekYear
  ) {
    this.form = new FormStore<TravelConfirmationFormData>({
      initialValues,
      schema: validationSchema,
      exposeAs: "travelConfirmationStore",
      onSubmit: () => Promise.resolve(),
      validate: ({ values }) => {
        const errors: FormErrors<TravelConfirmationFormData> = {};

        const actualTotal = sumBy(
          values.weeks.filter((x) => x.isTravel),
          (x) => toFinite(x.cost)
        );

        if (actualTotal?.toFixed(2) !== toFinite(values.totalCost)?.toFixed(2)) {
          errors.totalCost =
            "Totala verkliga kostnaden matchar inte fördelade beloppet över veckorna";
        }
        if (
          this.bookingData?.Consultant?.LegalPersonType ===
            LegalPersonType.Subcontractor &&
          !values.totalExVAT
        ) {
          errors.totalExVAT = locale.TotalExcludeVAT;
        }

        values.weeks.forEach((x, i) => {
          if (!x.isTravel) {
            return;
          }

          if (toFinite(x.cost) <= 0) {
            errors[`weeks.${i}.cost`] = "Var god fyll i kostnad";
          }
        });

        return errors;
      }
    });
  }

  isWeekDisabled(weekYear: WeekYear) {
    const weekExt = this.bookingData.BookingGroup.bookings.find((x) => {
      const w = dateToWeekYear(x.Week);

      return w.Week === weekYear.Week && w.Year === weekYear.Year;
    });

    return weekExt ? weekExt.IsConsultantPayed : false;
  }

  @action submit = async () => {
    const { values } = this.form;
    this.form.setSubmitting(true);

    try {
      const files = [];

      for (const file of this.form.values.files) {
        await UploadBlob.func({ file }).then((x) => files.push(x.data.uploadBlob));
      }

      const input: ConfirmTravelInput = {
        bookingId: this.bookingId,
        files,
        weeks: values.weeks.map((x) => ({
          weekYear: x.weekYear,
          description: x.description,
          startDate: x.startDate,
          endDate: x.endDate,
          cost: toFinite(x.cost),
          isTravel: x.isTravel,
          note: x.note
        })),
        totalCost: values.totalCost,
        totalExVAT: values.totalExVAT
      };

      const promise = this.isNew
        ? ConfimTravel.func(
            { input },
            {
              refetchQueries: [
                BookingHospitalityGet.OperationName,
                BookingScheduleSearch_Get.OperationName
              ]
            }
          )
        : UpdateTravelConfirmation.func(
            { id: this.confirmationId, input },
            {
              refetchQueries: [
                BookingHospitalityGet.OperationName,
                BookingScheduleSearch_Get.OperationName
              ]
            }
          );
      await promise;
    } catch (e) {
      globalStore().snackbars.error(`Cannot save form: \n${e}`);
    } finally {
      this.form.setSubmitting(false);
    }
  };

  // isWeekDisabled(weekYear: WeekYear) {
  //   const weekExt = this.bookingData.BookingGroup.WeeksExt.find(x => {
  //     const w = dateToWeekYear(x.Week);
  //
  //     return w.Week === weekYear.Week && w.Year === weekYear.Year;
  //   });
  //
  //   return weekExt ? !weekExt.IsOpen : false;
  // }

  @action setWeeks = (value: CustomDuration[]) => {
    const currentWeekYears = this.form.values.weeks.map((x) =>
      weekYearToString(x.weekYear)
    );

    const weekYears = durationsToWeeks(value, true)
      .map(dateToWeekYear)
      .map(weekYearToString);

    const newWeeks = weekYears.filter((x) => !currentWeekYears.includes(x));
    const removeWeeks = weekYears.filter((x) => currentWeekYears.includes(x));

    this.form.values.weeks = this.form.values.weeks.filter((x) =>
      removeWeeks.includes(weekYearToString(x.weekYear))
    );

    this.form.values.weeks.push(
      ...newWeeks.map((x) => {
        const parsed = parseWeekYear(x);

        return {
          weekYear: { Week: parsed.week, Year: parsed.year },
          cost: "0",
          description: null,
          startDate: null,
          endDate: null,
          isTravel: false,
          note: null
        };
      })
    );

    this.form.validate();
    this.form.changed();
  };

  get weeksValue() {
    if (!this.form.values.weeks) {
      return [];
    }

    return weeksToDurations(
      this.form.values.weeks?.map((x) => weekYearToDate(x.weekYear))
    );
  }

  @computed get travelWeekValue() {
    if (!this.form.values.weeks) {
      return [];
    }

    return weeksToDurations(
      this.form.values.weeks
        .filter((x) => x.isTravel)
        .map((x) => weekYearToDate(x.weekYear))
    );
  }

  @action setTravelWeek = (value: CustomDuration[]) => {
    const currentTaxWeekYears = this.form.values.weeks
      .filter((x) => x.isTravel)
      .map((x) => weekYearToString(x.weekYear));

    const weekYears = durationsToWeeks(value, true)
      .map(dateToWeekYear)
      .map(weekYearToString);

    const newWeeks = weekYears.filter((x) => !currentTaxWeekYears.includes(x));
    const removeWeeks = currentTaxWeekYears.filter((x) => !weekYears.includes(x));

    newWeeks.forEach((w) => {
      const parsed = parseWeekYear(w);
      const found = this.form.values.weeks.find(
        (x) => weekYearToString(x.weekYear) === w
      );

      if (found) {
        found.isTravel = true;
        found.cost = "0";
      } else {
        this.form.values.weeks.push({
          weekYear: {
            Week: parsed.week,
            Year: parsed.year
          },
          isTravel: true,
          cost: "0",
          description: null,
          startDate: null,
          endDate: null,
          note: null
        });
      }
    });

    removeWeeks.forEach((w) => {
      const found = this.form.values.weeks.find(
        (x) => weekYearToString(x.weekYear) === w
      );

      if (found) {
        found.isTravel = false;
        found.cost = "0";
        found.description = null;
        found.startDate = null;
        found.endDate = null;
      }
    });

    this.form.validate();
    this.form.changed();
  };
}
